import { useMemo, useRef, useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import AnchorShotStrip from '@/components/AnchorShotStrip';
import { Film, AlertTriangle } from 'lucide-react';

function parseScriptBlock(text) {
  // Split into lines and classify
  const lines = text.split('\n');
  return lines.map((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) return { type: 'blank', text: '', key: idx };
    // Character name: all caps, possibly with (V.O.) (O.S.) etc.
    if (/^[A-Z][A-Z\s.'()-]{1,40}$/.test(trimmed) && !trimmed.startsWith('INT.') && !trimmed.startsWith('EXT.')) {
      return { type: 'character', text: trimmed, key: idx };
    }
    // Parenthetical
    if (/^\(.*\)$/.test(trimmed)) {
      return { type: 'parenthetical', text: trimmed, key: idx };
    }
    // Scene heading
    if (/^(INT\.|EXT\.|INT\/EXT\.)/.test(trimmed)) {
      return { type: 'heading', text: trimmed, key: idx };
    }
    // Transition
    if (/^(CUT TO:|FADE TO:|DISSOLVE TO:|SMASH CUT:|MATCH CUT:)/.test(trimmed) || /^[A-Z\s]+:$/.test(trimmed)) {
      return { type: 'transition', text: trimmed, key: idx };
    }
    return { type: 'action', text: line, key: idx };
  });
}

function ScriptLine({ block }) {
  const styles = {
    character: 'text-center font-bold text-[var(--text-primary)] text-sm tracking-wide py-0.5',
    parenthetical: 'text-center italic text-[var(--text-muted)] text-xs pl-16 pr-24',
    heading: 'font-bold text-[var(--text-primary)] text-sm uppercase tracking-wider py-1 border-b border-[var(--border-subtle)]/30 mb-1',
    transition: 'text-right font-semibold text-[var(--text-muted)] text-xs uppercase tracking-wider py-1',
    action: 'text-[var(--text-secondary)] text-sm leading-relaxed',
    blank: 'h-3',
  };

  return (
    <div className={styles[block.type] || styles.action}>
      {block.text}
    </div>
  );
}

export default function VisualScreenplayView({ content, heading, sceneNumber, projectId, anchors, onAnchorSelect }) {
  const scrollRef = useRef(null);

  // Sort anchors by start_offset
  const sortedAnchors = useMemo(() => {
    if (!anchors || anchors.length === 0) return [];
    return [...anchors].sort((a, b) => (a.start_offset || 0) - (b.start_offset || 0));
  }, [anchors]);

  // Build segments: text blocks interspersed with anchor strips
  const segments = useMemo(() => {
    if (!content) return [];

    if (sortedAnchors.length === 0) {
      return [{ type: 'text', content: content, key: 'full' }];
    }

    const result = [];
    let lastEnd = 0;

    for (const anchor of sortedAnchors) {
      const start = anchor.start_offset || 0;
      const end = anchor.end_offset || start;

      // Text before this anchor
      if (start > lastEnd) {
        result.push({
          type: 'text',
          content: content.slice(lastEnd, start),
          key: `text-${lastEnd}`,
        });
      }

      // The anchored text (highlighted)
      result.push({
        type: 'anchor-text',
        content: content.slice(start, end),
        anchor,
        key: `anchor-${anchor.id}`,
      });

      // The shot strip
      result.push({
        type: 'strip',
        anchor,
        key: `strip-${anchor.id}`,
      });

      lastEnd = end;
    }

    // Remaining text after last anchor
    if (lastEnd < content.length) {
      result.push({
        type: 'text',
        content: content.slice(lastEnd),
        key: `text-${lastEnd}`,
      });
    }

    return result;
  }, [content, sortedAnchors]);

  if (!content) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Film size={32} className="mx-auto mb-3 text-[var(--text-disabled)]" />
          <p className="text-sm text-[var(--text-muted)]">No script content</p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="max-w-3xl mx-auto px-8 py-6" ref={scrollRef}>
        {/* Scene heading */}
        {heading && (
          <div className="mb-6 pb-4 border-b border-[var(--border-default)]">
            <h2 className="text-lg font-bold text-[var(--text-primary)] uppercase tracking-wider">
              {heading}
            </h2>
          </div>
        )}

        {/* No anchors banner */}
        {sortedAnchors.length === 0 && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-[var(--color-gold-500)]/5 border border-[var(--color-gold-500)]/20">
            <p className="text-xs text-[var(--color-gold-400)] flex items-center gap-2">
              <AlertTriangle size={12} />
              No shot anchors yet. Switch to "Script & Shots" tab to plan shots, or use Auto Plan.
            </p>
          </div>
        )}

        {/* Segments */}
        {segments.map((seg) => {
          if (seg.type === 'text') {
            const blocks = parseScriptBlock(seg.content);
            return (
              <div key={seg.key} className="font-mono">
                {blocks.map((block) => (
                  <ScriptLine key={block.key} block={block} />
                ))}
              </div>
            );
          }

          if (seg.type === 'anchor-text') {
            const blocks = parseScriptBlock(seg.content);
            return (
              <div
                key={seg.key}
                className="font-mono bg-[var(--color-gold-500)]/8 border-l-2 border-[var(--color-gold-500)]/40 pl-3 -ml-3 rounded-r"
              >
                {blocks.map((block) => (
                  <ScriptLine key={block.key} block={block} />
                ))}
              </div>
            );
          }

          if (seg.type === 'strip') {
            return (
              <AnchorShotStrip
                key={seg.key}
                anchor={seg.anchor}
                projectId={projectId}
                onSelect={(a) => onAnchorSelect?.(a.id)}
              />
            );
          }

          return null;
        })}

        {/* Bottom padding for scroll */}
        <div className="h-24" />
      </div>
    </ScrollArea>
  );
}
